import { useState, useEffect } from 'react'
import { ExternalLink } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { formatElapsed, tenantStatusVariant } from '@/lib/utils'
import type { TTenant, THealthCheck } from '@/types'

// ── Health status helpers ───────────────────────────────────────────────────

function healthColor(healthCheck: THealthCheck | undefined, status: string): string {
  if (status === 'provisioning') return 'var(--status-provisioning)'
  if (status === 'dormant') return 'var(--status-unknown)'
  if (!healthCheck) return 'var(--status-unknown)'
  if (healthCheck.is_healthy) return 'var(--status-healthy)'
  return 'var(--status-unhealthy)'
}

function healthLabel(healthCheck: THealthCheck | undefined, status: string): string {
  if (status === 'provisioning') return 'Provisioning'
  if (status === 'dormant') return 'Dormant'
  if (status === 'suspended') return 'Suspended'
  if (status === 'provision_failed') return 'Failed'
  if (!healthCheck) return 'Unknown'
  if (healthCheck.is_healthy) return 'Healthy'
  return 'Unhealthy'
}

// ── TenantCard ─────────────────────────────────────────────────────────────

interface TenantCardProps {
  tenant: TTenant
  healthCheck?: THealthCheck
  onDetailsClick: () => void
  animationDelay?: number
}

export function TenantCard({ tenant, healthCheck, onDetailsClick, animationDelay = 0 }: TenantCardProps) {
  const isProvisioning = tenant.status === 'provisioning'
  const color = healthColor(healthCheck, tenant.status)
  const label = healthLabel(healthCheck, tenant.status)
  const shouldPulse = isProvisioning || healthCheck?.is_healthy === true
  const rawUrl = tenant.tenant_url || `${tenant.slug}.deconstraint.ai`
  const url = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`

  return (
    <div
      className="card-hover fade-slide-up"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        borderLeft: `3px solid ${color}`,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        animationDelay: `${animationDelay}ms`,
      }}
    >
      {/* Top row: Name + Status badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
      }}>
        <span style={{
          fontSize: '15px',
          fontWeight: 600,
          color: 'var(--text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {tenant.name}
        </span>
        <Badge variant={tenantStatusVariant(tenant.status)}>
          {tenant.status.replace('_', ' ')}
        </Badge>
      </div>

      {/* URL row */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        style={{
          fontSize: '12px',
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '-6px',
        }}
      >
        {url}
        <ExternalLink size={11} style={{ flexShrink: 0, opacity: 0.6 }} />
      </a>

      {/* Health row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span
          className={shouldPulse ? 'animate-pulse-dot' : undefined}
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <span style={{
          fontSize: '13px',
          fontWeight: 500,
          color: color,
        }}>
          {isProvisioning ? (
            <ProvisioningTimer tenant={tenant} />
          ) : (
            label
          )}
        </span>
      </div>

      {/* Versions row */}
      {tenant.web_app_version && (
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
        }}>
          {tenant.web_app_version && (
            <span style={{
              fontSize: '11px',
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-tertiary)',
              backgroundColor: 'var(--bg-tertiary)',
              padding: '2px 6px',
              borderRadius: 'var(--radius-sm)',
            }}>
              web {tenant.web_app_version}
            </span>
          )}
          {tenant.openclaw_version && (
            <span style={{
              fontSize: '11px',
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-tertiary)',
              backgroundColor: 'var(--bg-tertiary)',
              padding: '2px 6px',
              borderRadius: 'var(--radius-sm)',
            }}>
              oc {tenant.openclaw_version}
            </span>
          )}
        </div>
      )}

      {/* Actions row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: 'auto',
        paddingTop: '4px',
      }}>
        <Button variant="secondary" size="sm" onClick={onDetailsClick}>
          Details
        </Button>
      </div>
    </div>
  )
}

// ── Provisioning Timer ─────────────────────────────────────────────────────

function ProvisioningTimer({ tenant }: { tenant: TTenant }) {
  const [, setTick] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(interval)
  }, [])
  return <span>Provisioning... {formatElapsed(tenant.created)}</span>
}
